import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Paper, Stack, TextField, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Block as BlockIcon, Refresh as RefreshIcon,
  Security as SecurityIcon, History as HistoryIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/ui';

const ACTION_TYPE_LABELS = {
  price_update: 'Изменить цену',
  ad_bid_update: 'Ставка рекламы',
  ad_campaign_pause: 'Пауза кампании',
  review_reply: 'Ответ на отзыв',
  supply_create: 'Создать поставку',
  recipe_save: 'Рецепт в базу',
  lesson_save: 'Урок в базу',
  other: 'Прочее',
};
const STATUS_LABELS = {
  executed: 'Выполнено', failed: 'Ошибка', measured: 'Измерено',
  executing: 'Выполняется', approved: 'Одобрено', pending: 'Ожидает',
  rejected: 'Отклонено', expired: 'Истёк',
};
const STATUS_COLORS = {
  executed: 'success', measured: 'success', failed: 'error',
  executing: 'info', approved: 'info', pending: 'warning',
  rejected: 'default', expired: 'default',
};

function formatDateTime(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('ru-RU');
  } catch (_) {
    return String(s);
  }
}

export default function AiSafety() {
  const notify = useNotify();
  const notifyRef = useRef(notify);
  useEffect(() => { notifyRef.current = notify; }, [notify]);

  const [loading, setLoading] = useState(false);
  const [emergencyStop, setEmergencyStop] = useState(false);
  const [autoExecuted24h, setAutoExecuted24h] = useState([]);
  const [rawSetting, setRawSetting] = useState(null);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/ai/safety/status');
      setEmergencyStop(!!data?.emergency_stop);
      setAutoExecuted24h(Array.isArray(data?.auto_executed_24h) ? data.auto_executed_24h : []);
      setRawSetting(data?.raw_setting || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Не удалось загрузить статус');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleToggle = (newValue) => {
    setPendingValue(newValue);
    setComment('');
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (pendingValue == null) return;
    setSaving(true);
    try {
      await axios.post('/ai/safety/emergency-stop', { enabled: pendingValue, comment: comment || null });
      notifyRef.current?.success?.(pendingValue ? 'EMERGENCY STOP включён' : 'EMERGENCY STOP снят');
      setConfirmOpen(false);
      setPendingValue(null);
      setComment('');
      await loadStatus();
    } catch (err) {
      notifyRef.current?.error?.(err?.response?.data?.message || err.message || 'Не удалось переключить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Безопасность AI"
        subtitle="Глобальный kill-switch и лента авто-исполненных действий"
        actions={
          <Button startIcon={<RefreshIcon />} onClick={loadStatus} disabled={loading}>
            Обновить
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Paper sx={{
        p: 4,
        mb: 3,
        textAlign: 'center',
        border: 2,
        borderColor: emergencyStop ? 'error.main' : 'success.main',
        bgcolor: emergencyStop ? 'error.lighter' : 'background.paper',
      }}>
        <SecurityIcon sx={{ fontSize: 64, color: emergencyStop ? 'error.main' : 'success.main', mb: 1 }} />
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          {emergencyStop ? 'AI EMERGENCY STOP активен' : 'AI работает в штатном режиме'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {emergencyStop
            ? 'Все auto-action\'ы заблокированы глобально. AI продолжает выдавать предложения, но реальные write-вызовы в маркетплейсы остановлены до снятия флага.'
            : 'Каждый аккаунт МП применяет свой уровень автономии (off / read_only / propose / auto_low / auto_full). Кнопка ниже моментально блокирует ВСЕ auto-действия.'}
        </Typography>
        {loading ? (
          <CircularProgress />
        ) : emergencyStop ? (
          <Button
            variant="outlined"
            color="success"
            size="large"
            onClick={() => handleToggle(false)}
            startIcon={<SecurityIcon />}
          >
            Снять EMERGENCY STOP
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={() => handleToggle(true)}
            startIcon={<BlockIcon />}
            sx={{ fontWeight: 'bold', py: 1.5, px: 4 }}
          >
            ВКЛЮЧИТЬ EMERGENCY STOP
          </Button>
        )}
      </Paper>

      {rawSetting?.updated_at && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Последнее изменение: {formatDateTime(rawSetting.updated_at)}
          {rawSetting?.value?.changed_by && ` — ${rawSetting.value.changed_by}`}
          {rawSetting?.value?.comment && ` («${rawSetting.value.comment}»)`}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <HistoryIcon color="action" />
          <Typography variant="h6">Auto-executed за последние 24 часа</Typography>
          <Chip size="small" label={`${autoExecuted24h.length}`} />
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>МП</TableCell>
                <TableCell>Правило</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Когда</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {autoExecuted24h.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    За 24 часа auto-action'ов не было
                  </TableCell>
                </TableRow>
              )}
              {autoExecuted24h.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{a.id}</TableCell>
                  <TableCell>{ACTION_TYPE_LABELS[a.action_type] || a.action_type}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={String(a.marketplace || '').toUpperCase()} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{a.auto_approve_rule || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLORS[a.status] || 'default'} label={STATUS_LABELS[a.status] || a.status} />
                  </TableCell>
                  <TableCell>{formatDateTime(a.executed_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingValue ? 'Включить EMERGENCY STOP?' : 'Снять EMERGENCY STOP?'}
        </DialogTitle>
        <DialogContent>
          <Alert severity={pendingValue ? 'error' : 'warning'} sx={{ mb: 2 }}>
            <AlertTitle>{pendingValue ? 'Внимание!' : 'Подтверждение'}</AlertTitle>
            {pendingValue
              ? 'После включения все auto-action\'ы будут заблокированы глобально. AI продолжит выдавать предложения, но НИКАКИЕ write-вызовы в WB/Ozon/Yandex не будут отправлены до снятия флага.'
              : 'После снятия auto-action\'ы снова станут возможны (с учётом уровня автономии каждого аккаунта).'}
          </Alert>
          <DialogContentText sx={{ mb: 2 }}>
            Изменение будет видно всей команде через Telegram-уведомление.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Комментарий (рекомендуется)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={pendingValue
              ? 'Причина включения (инцидент, тест, аномалия...)'
              : 'Что починили / решили (опционально)'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Отмена</Button>
          <Button
            variant="contained"
            color={pendingValue ? 'error' : 'success'}
            onClick={handleConfirm}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : (pendingValue ? <BlockIcon /> : <SecurityIcon />)}
          >
            {pendingValue ? 'Да, включить' : 'Да, снять'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
